import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import PageShell from '../components/Layout/PageShell'
import PortalHeader from '../components/Layout/PortalHeader'
import { PORTAL_ROLES } from '../components/Sidebar/portalNav'
import { PREFERRED_LANGUAGES, parseDefaultLanguageCode } from '../constants/preferredLanguages'
import {
  SettingsCard,
  SettingsToggle,
  SettingsAvatar,
  SettingsBtnGroup,
} from '../components/Settings/SettingsPortalUi'
import { RsIcon, IconLabel } from '../components/icons'
import axios from 'axios'
import toast from 'react-hot-toast'
import { toastEmailCodeResult } from '../utils/mailFeedback'

function formatPaymentStatus(status) {
  const key = String(status || '').toLowerCase()
  if (key === 'paid' || key === 'completed' || key === 'active') return 'Paid'
  if (key === 'awaiting_admin') return 'Pending review'
  if (key === 'cancelled') return 'Cancelled'
  return status || '—'
}

function PaymentHistoryList({ items, loading, variant = 'portal' }) {
  if (loading) {
    return <p className={variant === 'portal' ? 'portal-info-label' : 'text-sm text-gray-500'}>Loading payment history…</p>
  }
  if (!items.length) {
    return (
      <p className={variant === 'portal' ? 'portal-info-label' : 'text-sm text-gray-500'}>
        No payment records yet.
      </p>
    )
  }
  if (variant === 'portal') {
    return items.map((payment) => (
      <div key={payment.id} className="portal-info-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div>
          <div className="portal-info-value">{payment.plan} Plan</div>
          <div className="portal-info-label" style={{ textTransform: 'none' }}>{payment.date}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="portal-info-value">{payment.amount}</div>
          <span style={{ fontSize: 11, color: '#67c23a', fontWeight: 600 }}>{payment.status}</span>
        </div>
      </div>
    ))
  }
  return (
    <div className="space-y-2">
      {items.map((payment) => (
        <div key={payment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div>
            <p className="text-sm font-medium text-navy">{payment.plan} Plan</p>
            <p className="text-xs text-gray-500">{payment.date}</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-semibold text-navy">{payment.amount}</p>
            <span className="badge badge-green text-xs">{payment.status}</span>
          </div>
        </div>
      ))}
    </div>
  )
}

export default function Settings() {
  const { user, logout, refreshUser } = useAuth()
  const [activeTab, setActiveTab] = useState('profile')
  const [loading, setLoading] = useState(false)
  const [avatarUploading, setAvatarUploading] = useState(false)
  const [defaultLanguage, setDefaultLanguage] = useState(() => parseDefaultLanguageCode(user))
  const [profile, setProfile] = useState({
    realName: user?.real_name || '',
    position: user?.position || '',
    phone: user?.phone || '',
    companyFullName: user?.company_full_name || '',
    companyShortName: user?.company_short_name || '',
  })
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: '',
  })
  const [companyInfo, setCompanyInfo] = useState(null)
  const [employmentHistory, setEmploymentHistory] = useState([])
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('LP')
  const [showTransfer, setShowTransfer] = useState(false)
  const [transferData, setTransferData] = useState({
    newEmail: '',
    verifyCode: '',
    oldPhone: user?.phone || '',
    companyFullName: '',
    companyShortName: '',
    position: '',
    realName: '',
  })
  const [notifPrefs, setNotifPrefs] = useState({
    dealUpdates: true,
    newMessages: true,
    meetingReminders: true,
    digest: true,
    marketing: false,
    pushUrgent: false,
  })
  const [paymentHistory, setPaymentHistory] = useState([])
  const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false)

  useEffect(() => {
    setDefaultLanguage(parseDefaultLanguageCode(user))
  }, [user?.id, user?.default_language, user?.registration_notes])

  useEffect(() => {
    if (!user) return
    setProfile({
      realName: user.real_name || '',
      position: user.position || '',
      phone: user.phone || '',
      companyFullName: user.company_full_name || '',
      companyShortName: user.company_short_name || '',
    })
  }, [user?.id, user?.real_name, user?.position, user?.phone, user?.company_full_name, user?.company_short_name])

  useEffect(() => {
    if (activeTab !== 'billing') return
    setPaymentHistoryLoading(true)
    axios.get('/api/payments/history')
      .then((res) => setPaymentHistory(res.data?.items || []))
      .catch(() => setPaymentHistory([]))
      .finally(() => setPaymentHistoryLoading(false))
  }, [activeTab])

  useEffect(() => {
    if (activeTab !== 'company') return
    const load = async () => {
      try {
        const [co, hist] = await Promise.all([
          axios.get('/api/companies/mine'),
          axios.get('/api/companies/employment-history'),
        ])
        setCompanyInfo(co.data)
        setEmploymentHistory(hist.data || [])
      } catch {
        /* optional */
      }
    }
    load()
  }, [activeTab])

  const handleSendInvite = async (e) => {
    e.preventDefault()
    if (!inviteEmail) return toast.error('Please enter invitee email')
    setLoading(true)
    try {
      const res = await axios.post('/api/companies/invites', {
        invitedEmail: inviteEmail,
        suggestedRole: inviteRole,
      })
      if (res.data.emailSent) {
        toast.success('Invitation email sent')
      } else {
        toast.success('Invitation created — forward the link manually if SMTP is not configured')
        if (res.data.inviteUrl) {
          navigator.clipboard?.writeText(res.data.inviteUrl)
          toast.success('Invite link copied to clipboard')
        }
      }
      setInviteEmail('')
      const co = await axios.get('/api/companies/mine')
      setCompanyInfo(co.data)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invitation failed')
    } finally {
      setLoading(false)
    }
  }

  const handleCancelInvite = async (id) => {
    try {
      await axios.delete(`/api/companies/invites/${id}`)
      toast.success('Invitation cancelled')
      const co = await axios.get('/api/companies/mine')
      setCompanyInfo(co.data)
    } catch {
      toast.error('Cancellation failed')
    }
  }

  const handleAvatarUpload = async (file) => {
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Please upload a JPG or PNG image')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be 5MB or smaller')
      return
    }
    setAvatarUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      await axios.post('/api/users/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      await refreshUser?.()
      toast.success('Photo updated')
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to upload photo')
    } finally {
      setAvatarUploading(false)
    }
  }

  const handleAvatarRemove = async () => {
    setAvatarUploading(true)
    try {
      await axios.delete('/api/users/avatar')
      await refreshUser?.()
      toast.success('Photo removed')
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove photo')
    } finally {
      setAvatarUploading(false)
    }
  }

  const handleUpdateProfile = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      await axios.put('/api/users/update-profile', { ...profile, defaultLanguage })
      await refreshUser?.()
      toast.success('Profile updated successfully!')
    } catch (err) {
      toast.error('Failed to update profile')
    } finally {
      setLoading(false)
    }
  }

  const handleChangePassword = async (e) => {
    e.preventDefault()
    if (passwords.new !== passwords.confirm) {
      toast.error('Passwords do not match')
      return
    }
    setLoading(true)
    try {
      await axios.post('/api/users/change-password', {
        currentPassword: passwords.current,
        newPassword: passwords.new,
      })
      toast.success('Password changed successfully!')
      setPasswords({ current: '', new: '', confirm: '' })
    } catch (err) {
      toast.error('Failed to change password')
    } finally {
      setLoading(false)
    }
  }

  const handleTransferCompany = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      await axios.post('/api/auth/transfer-company', {
        oldPhone: transferData.oldPhone,
        newEmail: transferData.newEmail,
        verifyCode: transferData.verifyCode,
        companyFullName: transferData.companyFullName,
        companyShortName: transferData.companyShortName,
        position: transferData.position,
        realName: transferData.realName,
      })
      toast.success('Company transfer successful! Please login again.')
      logout()
    } catch (err) {
      toast.error('Transfer failed')
    } finally {
      setLoading(false)
    }
  }

  const tabs = [
    { id: 'profile', label: 'Profile', icon: 'user' },
    { id: 'company', label: 'Company', icon: 'building2' },
    { id: 'security', label: 'Security', icon: 'lock' },
    { id: 'notifications', label: 'Notifications', icon: 'bell' },
    { id: 'billing', label: 'Billing', icon: 'creditCard' },
  ]

  const isPortal = PORTAL_ROLES.includes(user?.role)

  return (
    <PageShell>
      <div className={isPortal ? 'portal-settings-page portal-page-full' : ''}>
        {isPortal ? (
          <PortalHeader title="Settings" showSearch={false} />
        ) : (
          <h1 className="text-2xl font-bold text-navy mb-8">Settings</h1>
        )}

        {isPortal && (
          <div className="portal-settings-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`portal-settings-tab${activeTab === tab.id ? ' active' : ''}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        <div className={isPortal ? '' : 'flex gap-8'}>
          {!isPortal && (
          <div className="w-64 flex-shrink-0">
            <div className="space-y-1">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <RsIcon name={tab.icon} size={20} className="w-5 h-5" />
                  <span>{tab.label}</span>
                  <RsIcon name="chevronRight" size={16} className="w-4 h-4 ml-auto" />
                </button>
              ))}
            </div>
          </div>
          )}

          <div className={isPortal ? '' : 'flex-1'}>
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              isPortal ? (
                <SettingsCard title="Profile Information" subtitle="Public profile visible to other users">
                  <SettingsAvatar
                    name={profile.realName || user?.real_name}
                    avatarUrl={user?.avatar_url}
                    onUpload={handleAvatarUpload}
                    onRemove={handleAvatarRemove}
                    uploading={avatarUploading}
                  />
                  <form onSubmit={handleUpdateProfile}>
                    <div className="portal-settings-form-row">
                      <div>
                        <label>Full Name</label>
                        <input
                          type="text"
                          value={profile.realName}
                          onChange={(e) => setProfile((prev) => ({ ...prev, realName: e.target.value }))}
                          className="input-field"
                        />
                      </div>
                      <div>
                        <label>Job Title</label>
                        <input
                          type="text"
                          value={profile.position}
                          onChange={(e) => setProfile((prev) => ({ ...prev, position: e.target.value }))}
                          className="input-field"
                        />
                      </div>
                    </div>
                    <div style={{ marginTop: 16 }}>
                      <label>Work Email</label>
                      <input type="email" value={user?.work_email || ''} disabled className="input-field" style={{ background: '#f8fafc' }} />
                      <p className="field-hint" style={{ fontSize: 11, color: '#909399', marginTop: 4 }}>✅ Verified · Email cannot be changed here</p>
                    </div>
                    <div style={{ marginTop: 16 }}>
                      <label>Phone Number</label>
                      <input
                        type="tel"
                        value={profile.phone}
                        onChange={(e) => setProfile((prev) => ({ ...prev, phone: e.target.value }))}
                        className="input-field"
                      />
                    </div>
                    <div style={{ marginTop: 16 }}>
                      <label>Default Language</label>
                      <select
                        className="input-field"
                        value={defaultLanguage}
                        onChange={(e) => setDefaultLanguage(e.target.value)}
                      >
                        {PREFERRED_LANGUAGES.map((lang) => (
                          <option key={lang.code} value={lang.code}>{lang.label}</option>
                        ))}
                      </select>
                      <p className="field-hint" style={{ fontSize: 11, color: '#909399', marginTop: 4 }}>
                        Incoming chat messages are automatically translated into this language.
                      </p>
                    </div>
                    <div className="portal-system-name-box">
                      <strong>System Name:</strong> {user?.system_name}
                      <div style={{ fontSize: 11, marginTop: 4, opacity: 0.85 }}>
                        Format: {user?.company_short_name}-{user?.real_name}-{user?.position}
                      </div>
                    </div>
                    <SettingsBtnGroup>
                      <button type="submit" disabled={loading} className="btn-primary">
                        {loading ? 'Saving…' : 'Save Changes'}
                      </button>
                      <button type="button" className="portal-settings-btn-outline" onClick={() => {
                        setProfile({
                          realName: user?.real_name || '',
                          position: user?.position || '',
                          phone: user?.phone || '',
                          companyFullName: user?.company_full_name || '',
                          companyShortName: user?.company_short_name || '',
                        })
                        setDefaultLanguage(parseDefaultLanguageCode(user))
                      }}>
                        Reset
                      </button>
                    </SettingsBtnGroup>
                  </form>
                </SettingsCard>
              ) : (
              <div className="card p-6">
                <h2 className="text-lg font-bold text-navy mb-6">Profile Information</h2>
                <form onSubmit={handleUpdateProfile} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                      <input
                        type="text"
                        value={profile.realName}
                        onChange={(e) => setProfile(prev => ({ ...prev, realName: e.target.value }))}
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
                      <input
                        type="text"
                        value={profile.position}
                        onChange={(e) => setProfile(prev => ({ ...prev, position: e.target.value }))}
                        className="input-field"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Work Email</label>
                    <input
                      type="email"
                      value={user?.work_email || ''}
                      disabled
                      className="input-field bg-gray-50"
                    />
                    <p className="text-xs text-gray-500 mt-1">Email cannot be changed. Use company transfer instead.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <input
                      type="tel"
                      value={profile.phone}
                      onChange={(e) => setProfile(prev => ({ ...prev, phone: e.target.value }))}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Default Language</label>
                    <select
                      className="input-field"
                      value={defaultLanguage}
                      onChange={(e) => setDefaultLanguage(e.target.value)}
                    >
                      {PREFERRED_LANGUAGES.map((lang) => (
                        <option key={lang.code} value={lang.code}>{lang.label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Incoming chat messages are automatically translated into this language.
                    </p>
                  </div>
                  <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                    <p className="text-sm text-primary-700">
                      <span className="font-semibold">System Name:</span> {user?.system_name}
                    </p>
                    <p className="text-xs text-primary-600 mt-1">Format: {user?.company_short_name}-{user?.real_name}-{user?.position}</p>
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary py-2.5 px-6 disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </form>
              </div>
              )
            )}

            {/* Company Tab */}
            {activeTab === 'company' && (
              <div className="space-y-6">
              {companyInfo?.company && (
                <div className="card p-6">
                  <h2 className="text-lg font-bold text-navy mb-4">Company plan & seats</h2>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-500">Plan</p>
                      <p className="font-bold text-navy">{companyInfo.plan?.name || companyInfo.company.plan_tier}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-500">Seats</p>
                      <p className="font-bold text-navy">{companyInfo.seatsUsed} / {companyInfo.seatsMax}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-500">Domain membership</p>
                      <p className="font-bold text-navy">{companyInfo.company.is_paid ? 'Active' : 'Inactive'}</p>
                    </div>
                  </div>
                  {companyInfo.storage && (
                    <div className="mt-4">
                      <p className="text-xs text-gray-500 mb-1">SP storage ({companyInfo.storage.percentUsed}%)</p>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-primary-500 h-2 rounded-full"
                          style={{ width: `${companyInfo.storage.percentUsed}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        {companyInfo.storage.usedGb?.toFixed(2)} GB / {companyInfo.storage.limitGb} GB
                      </p>
                    </div>
                  )}
                  <div className="mt-4">
                    <p className="text-sm font-semibold text-navy mb-2">Members</p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {companyInfo.members?.map((m) => (
                        <li key={m.id}>{m.system_name} · {m.role} · {m.work_email}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {companyInfo?.canManage && (
                <div className="card p-6">
                  <h2 className="text-lg font-bold text-navy mb-4">Invite colleagues (sub-accounts)</h2>
                  <p className="text-sm text-gray-500 mb-4">
                    Invitee email must match your company domain. Registration links are valid for 7 days and use one seat.
                  </p>
                  <form onSubmit={handleSendInvite} className="flex flex-wrap gap-3 items-end">
                    <div className="flex-1 min-w-[200px]">
                      <label className="block text-xs text-gray-500 mb-1">Work email</label>
                      <input
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        className="input-field"
                        placeholder={`name@${companyInfo.company?.email_domain || 'company.com'}`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Role</label>
                      <select
                        value={inviteRole}
                        onChange={(e) => setInviteRole(e.target.value)}
                        className="input-field"
                      >
                        {['GP', 'LP', 'Startup', 'ServiceProvider'].map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </div>
                    <button type="submit" disabled={loading} className="btn-primary px-6 py-2.5">
                      Send invitation
                    </button>
                  </form>
                  {companyInfo.invites?.length > 0 && (
                    <div className="mt-4 border-t pt-4">
                      <p className="text-sm font-semibold mb-2">Pending invitations</p>
                      {companyInfo.invites.filter((i) => i.status === 'pending').map((inv) => (
                        <div key={inv.id} className="flex justify-between items-center text-sm py-2">
                          <span>{inv.invited_email} → {inv.suggested_role}</span>
                          <button
                            type="button"
                            onClick={() => handleCancelInvite(inv.id)}
                            className="text-red-500 text-xs hover:underline"
                          >
                            Cancel
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {employmentHistory.length > 0 && (
                <div className="card p-6">
                  <h2 className="text-lg font-bold text-navy mb-4">Employment history</h2>
                  <ul className="text-sm space-y-2">
                    {employmentHistory.map((h) => (
                      <li key={h.id} className="text-gray-600">
                        {h.event_type} · {h.company_full_name || h.work_email} · {String(h.created_at).slice(0, 10)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="card p-6">
                <h2 className="text-lg font-bold text-navy mb-6">Company Information</h2>
                <form onSubmit={handleUpdateProfile} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company Full Name</label>
                    <input
                      type="text"
                      value={profile.companyFullName}
                      onChange={(e) => setProfile(prev => ({ ...prev, companyFullName: e.target.value }))}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company Short Name</label>
                    <input
                      type="text"
                      value={profile.companyShortName}
                      onChange={(e) => setProfile(prev => ({ ...prev, companyShortName: e.target.value }))}
                      className="input-field"
                    />
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-700">
                      <span className="font-semibold">⚠️ Changing companies:</span> Verify with your original phone number to bind your account to a new work email. <strong>Your previous company email can no longer be used to sign in</strong>; employment history is kept in the list below.
                    </p>
                    <button
                      onClick={() => setShowTransfer(true)}
                      className="mt-2 text-sm text-primary-500 hover:text-primary-600 font-medium"
                    >
                      Initiate Company Transfer →
                    </button>
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary py-2.5 px-6 disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </form>

                {/* Transfer Modal */}
                {showTransfer && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl w-full max-w-md p-6">
                      <div className="flex justify-between items-center mb-6">
                        <h3 className="text-lg font-bold text-navy">Company Transfer</h3>
                        <button onClick={() => setShowTransfer(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                          <RsIcon name="close" size={20} className="w-5 h-5 text-gray-500" />
                        </button>
                      </div>
                      <form onSubmit={handleTransferCompany} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Original Phone Number</label>
                          <input
                            type="tel"
                            value={transferData.oldPhone}
                            onChange={(e) => setTransferData(prev => ({ ...prev, oldPhone: e.target.value }))}
                            className="input-field"
                            placeholder="Verify with original phone"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">New Work Email</label>
                          <input
                            type="email"
                            value={transferData.newEmail}
                            onChange={(e) => setTransferData(prev => ({ ...prev, newEmail: e.target.value }))}
                            className="input-field"
                            placeholder="new-email@company.com"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Email Verification Code</label>
                          <div className="flex gap-3">
                            <input
                              type="text"
                              value={transferData.verifyCode}
                              onChange={(e) => setTransferData(prev => ({ ...prev, verifyCode: e.target.value }))}
                              className="input-field flex-1"
                              placeholder="Enter code"
                              required
                            />
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  const res = await axios.post('/api/auth/transfer-company/send-code', {
                                    newEmail: transferData.newEmail,
                                  })
                                  toastEmailCodeResult(res, toast)
                                } catch (err) {
                                  toast.error(err.response?.data?.message || 'Failed to send')
                                }
                              }}
                              className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600"
                            >
                              Send Code
                            </button>
                          </div>
                        </div>
                        <div className="flex gap-3 pt-4">
                          <button
                            type="button"
                            onClick={() => setShowTransfer(false)}
                            className="flex-1 py-2.5 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50"
                          >
                            {loading ? 'Processing...' : 'Transfer Account'}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </div>
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              isPortal ? (
                <SettingsCard title="Security" subtitle="Password and authentication">
                  <form onSubmit={handleChangePassword}>
                    <div style={{ marginBottom: 16 }}>
                      <label>Current Password</label>
                      <input
                        type="password"
                        value={passwords.current}
                        onChange={(e) => setPasswords((prev) => ({ ...prev, current: e.target.value }))}
                        className="input-field"
                        placeholder="Enter current password"
                      />
                    </div>
                    <div className="portal-settings-form-row">
                      <div>
                        <label>New Password</label>
                        <input
                          type="password"
                          value={passwords.new}
                          onChange={(e) => setPasswords((prev) => ({ ...prev, new: e.target.value }))}
                          className="input-field"
                          placeholder="Min 8 characters"
                          minLength={8}
                        />
                      </div>
                      <div>
                        <label>Confirm New Password</label>
                        <input
                          type="password"
                          value={passwords.confirm}
                          onChange={(e) => setPasswords((prev) => ({ ...prev, confirm: e.target.value }))}
                          className="input-field"
                          placeholder="Repeat new password"
                        />
                      </div>
                    </div>
                    <SettingsBtnGroup>
                      <button type="submit" disabled={loading} className="btn-primary">
                        {loading ? 'Changing…' : 'Change Password'}
                      </button>
                    </SettingsBtnGroup>
                  </form>
                </SettingsCard>
              ) : (
              <div className="card p-6">
                <h2 className="text-lg font-bold text-navy mb-6">Security</h2>
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                    <input
                      type="password"
                      value={passwords.current}
                      onChange={(e) => setPasswords(prev => ({ ...prev, current: e.target.value }))}
                      className="input-field"
                      placeholder="Enter current password"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                    <input
                      type="password"
                      value={passwords.new}
                      onChange={(e) => setPasswords(prev => ({ ...prev, new: e.target.value }))}
                      className="input-field"
                      placeholder="Min 8 characters"
                      minLength={8}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                    <input
                      type="password"
                      value={passwords.confirm}
                      onChange={(e) => setPasswords(prev => ({ ...prev, confirm: e.target.value }))}
                      className="input-field"
                      placeholder="Repeat new password"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary py-2.5 px-6 disabled:opacity-50"
                  >
                    {loading ? 'Changing...' : 'Change Password'}
                  </button>
                </form>
              </div>
              )
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              isPortal ? (
                <SettingsCard title="Notification Preferences" subtitle="Choose how you receive notifications">
                  <div className="portal-settings-section-title">
                    <IconLabel name="email">Email Notifications</IconLabel>
                  </div>
                  <SettingsToggle
                    icon="inbox"
                    iconClass="blue"
                    name="Deal Updates"
                    desc="Get notified when a deal you're tracking is updated, funded, or closed"
                    checked={notifPrefs.dealUpdates}
                    onChange={(v) => setNotifPrefs((p) => ({ ...p, dealUpdates: v }))}
                  />
                  <SettingsToggle
                    icon="chat"
                    iconClass="blue"
                    name="New Messages"
                    desc="Receive email alerts for new chat messages"
                    checked={notifPrefs.newMessages}
                    onChange={(v) => setNotifPrefs((p) => ({ ...p, newMessages: v }))}
                  />
                  <SettingsToggle
                    icon="calendar"
                    iconClass="blue"
                    name="Meeting Reminders"
                    desc="Get email reminders 30 minutes before scheduled meetings"
                    checked={notifPrefs.meetingReminders}
                    onChange={(v) => setNotifPrefs((p) => ({ ...p, meetingReminders: v }))}
                  />
                  <div className="portal-settings-section-title" style={{ marginTop: 24 }}>Other</div>
                  <SettingsToggle
                    icon="bell"
                    iconClass="orange"
                    name="Weekly Digest"
                    desc="Summary of platform activity and market insights"
                    checked={notifPrefs.digest}
                    onChange={(v) => setNotifPrefs((p) => ({ ...p, digest: v }))}
                  />
                  <SettingsToggle
                    icon="zap"
                    iconClass="purple"
                    name="Push — Urgent Actions"
                    desc="Browser push for time-sensitive deal actions"
                    checked={notifPrefs.pushUrgent}
                    onChange={(v) => setNotifPrefs((p) => ({ ...p, pushUrgent: v }))}
                  />
                  <SettingsToggle
                    icon="megaphone"
                    iconClass="green"
                    name="Marketing Emails"
                    desc="Product updates and platform announcements"
                    checked={notifPrefs.marketing}
                    onChange={(v) => setNotifPrefs((p) => ({ ...p, marketing: v }))}
                  />
                </SettingsCard>
              ) : (
              <div className="card p-6">
                <h2 className="text-lg font-bold text-navy mb-6">Notification Preferences</h2>
                <div className="space-y-4">
                  {[
                    { label: 'Email notifications for new messages', checked: true },
                    { label: 'Email notifications for meeting invites', checked: true },
                    { label: 'Email notifications for deal updates', checked: true },
                    { label: 'Push notifications for urgent actions', checked: false },
                    { label: 'Weekly digest email', checked: true },
                    { label: 'Marketing emails', checked: false },
                  ].map((item, idx) => (
                    <label key={idx} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                      <span className="text-sm text-gray-700">{item.label}</span>
                      <div className={`w-11 h-6 rounded-full transition-colors ${item.checked ? 'bg-primary-500' : 'bg-gray-300'} relative`}>
                        <div className={`w-5 h-5 rounded-full bg-white absolute top-0.5 transition-transform ${item.checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              )
            )}

            {/* Billing Tab */}
            {activeTab === 'billing' && (
              isPortal ? (
                <SettingsCard title="Billing & Subscription" subtitle="Manage your plan and payment history">
                  <div className={`portal-plan-card${user?.is_paid ? ' current' : ''}`}>
                    <div style={{ flex: 1 }}>
                      <div className="portal-plan-name">
                        {user?.plan_tier || 'Free'} Plan
                        {user?.is_paid && <span className="portal-plan-badge">Current</span>}
                      </div>
                      <div className="portal-plan-price">
                        {user?.is_paid ? 'Active' : '¥0'} <span>/ month</span>
                      </div>
                      <ul style={{ listStyle: 'none', padding: 0, fontSize: 12.5, color: '#606266' }}>
                        <li>✓ Chat with verified GP/LP contacts</li>
                        <li>✓ Document storage & translation</li>
                        <li>✓ Email campaigns (with approval)</li>
                      </ul>
                    </div>
                    <button type="button" className="btn-primary" onClick={() => { window.location.href = '/payment' }}>
                      {user?.is_paid ? 'Manage Plan' : 'Upgrade →'}
                    </button>
                  </div>
                  <div className="portal-settings-section-title" style={{ marginTop: 8 }}>Payment History</div>
                  <PaymentHistoryList items={paymentHistory} loading={paymentHistoryLoading} variant="portal" />
                </SettingsCard>
              ) : (
              <div className="card p-6">
                <h2 className="text-lg font-bold text-navy mb-6">Billing & Subscription</h2>
                <div className="space-y-6">
                  <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm font-semibold text-primary-700">Current Plan</p>
                        <p className="text-2xl font-bold text-primary-900">{user?.plan_tier || 'Free'}</p>
                      </div>
                      <button
                        onClick={() => window.location.href = '/payment'}
                        className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600"
                      >
                        Upgrade
                      </button>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-navy mb-3">Payment History</h3>
                    <PaymentHistoryList items={paymentHistory} loading={paymentHistoryLoading} variant="card" />
                  </div>
                </div>
              </div>
              )
            )}
          </div>
        </div>
      </div>
    </PageShell>
  )
}
